// Generated (not hand-drawn) per-variety icon: a real, distinct little
// illustration driven by a variety's own {shape, size, color} spec from
// GET /api/plant-varieties (see backend/services/plant_varieties.py for
// why this is procedural rather than illustrated art for this first slice).
// Renders nothing (caller falls back to the plain emoji) when `spec` is
// missing -- an unknown/unset variety, a custom name, or a species this
// first slice's catalog doesn't cover yet are all real, valid states, not
// errors.

const COLOR_FILL = {
    red: '#c9432f',
    orange: '#dd8a37',
    yellow: '#e5c13f',
    green: '#6f9a4c',
    green_striped: '#79a350',
    dark_purple: '#4f3345',
    pink: '#dd8a97',
    brown: '#8a6240',
};

const SIZE_SCALE = { small: 0.68, medium: 0.84, large: 1 };

// Each shape returns the fill path/ellipse for a 64x64 viewBox, centered
// around (32, 36) so every variant sits under the same calyx position.
function bodyPath(shape) {
    switch (shape) {
        case 'elongated': // Roma, San Marzano
            return <ellipse cx="32" cy="36" rx="13" ry="21" />;
        case 'oval': // Plum
            return <ellipse cx="32" cy="36" rx="17" ry="20" />;
        case 'pear': // Yellow Pear
            return <path d="M32 16c7 6 12 15 12 24a12 12 0 1 1-24 0c0-9 5-18 12-24z" />;
        case 'ribbed': // Beefsteak, Black Krim, Pineapple — lobed silhouette
            return (
                <path d="M32 15c3 0 5 2 7 3 3-1 6 0 8 3 2 3 2 6 1 9 2 2 3 5 3 8
                    0 10-9 18-19 18s-19-8-19-18c0-3 1-6 3-8-1-3-1-6 1-9 2-3 5-4 8-3
                    2-1 4-3 7-3z" />
            );
        case 'round': // Cherry, Bambino, Green Zebra, Black Cherry, Yellow Cherry
        default:
            return <ellipse cx="32" cy="36" rx="19" ry="19" />;
    }
}

function ribLines(shape) {
    // A couple of soft internal curves read as "ribbed/lobed" for the
    // beefsteak-family shape, and as a faint segment line on a plain round
    // body — a cheap, real visual difference, not just a silhouette change.
    if (shape !== 'ribbed') return null;
    return (
        <g stroke="rgba(0,0,0,0.14)" strokeWidth="1.4" fill="none" strokeLinecap="round">
            <path d="M32 19 C 28 27, 28 45, 32 53" />
            <path d="M32 19 C 36 27, 36 45, 32 53" />
            <path d="M17 32 C 24 30, 40 30, 47 32" />
        </g>
    );
}

function stripes(color) {
    if (color !== 'green_striped') return null;
    return (
        <g stroke="rgba(90,70,20,0.35)" strokeWidth="1.6" fill="none" strokeLinecap="round">
            <path d="M20 24 C 26 40, 26 46, 22 52" />
            <path d="M32 17 C 32 34, 32 46, 32 55" />
            <path d="M44 24 C 38 40, 38 46, 42 52" />
        </g>
    );
}

function Calyx({ scale }) {
    // The small green star/cap every tomato variant keeps, so the whole
    // family still reads as "tomato" at a glance regardless of body shape.
    const s = 15 + 4 * scale;
    return (
        <g transform={`translate(32 ${16 - 2 * scale})`} fill="#5c8a3e">
            {[0, 72, 144, 216, 288].map((deg) => (
                <path
                    key={deg}
                    transform={`rotate(${deg})`}
                    d={`M0 0 L${s * 0.32} -${s * 0.55} L0 -${s} L-${s * 0.32} -${s * 0.55} Z`}
                />
            ))}
        </g>
    );
}

export default function VarietyIcon({ spec, size = 40, className = '' }) {
    if (!spec || !spec.shape) return null;
    const scale = SIZE_SCALE[spec.size] || 0.84;
    const fill = COLOR_FILL[spec.color] || COLOR_FILL.red;
    return (
        <svg
            viewBox="0 0 64 64"
            width={size}
            height={size}
            className={className}
            role="img"
            aria-label={spec.label || 'variety icon'}
        >
            <g transform={`translate(32 36) scale(${scale}) translate(-32 -36)`}>
                <g fill={fill}>{bodyPath(spec.shape)}</g>
                {ribLines(spec.shape)}
                {stripes(spec.color)}
                <Calyx scale={scale} />
            </g>
        </svg>
    );
}
